import React, { useEffect, useRef, memo, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import {
    Chart, LineController, LineElement, Filler, PointElement, LinearScale, TimeScale, Tooltip, Interaction, CategoryScale
} from 'chart.js';
import 'chartjs-adapter-moment';
import moment from 'moment';
import numeral from 'numeral';
import ChartLoadingPlaceholder from './ChartLoadingPlaceholder';
Chart.register(LineController, LineElement, Filler, PointElement, LinearScale, TimeScale, Tooltip, CategoryScale);

const BaseTimeseriesAPIChart = memo(function BaseTimeseriesAPIChart({
   title,
   timeframe,
   data = {},
   periodStart,
   periodEnd,
   periodSelected,
   currency
}) {
    const navigate = useNavigate();
    const { rows, prevRows, isLoading, error } = data;
    // const { rows, prevRows, error } = data; 
    // let isLoading = true;
    const chartInstanceRef = useRef(null);
    const canvas = useRef(null);

    const formatNumber = useCallback((number) => {
        if (!currency) {
            return typeof number === 'number' && !Number.isInteger(number) 
                ? number.toFixed(2) 
                : number;
        }
        if (typeof number === "undefined") return null;
        return numeral(number).format('$0a').toUpperCase();
    }, [currency]);

    const convertToDate = useCallback((data) => {
        if (!data) return [];
        
        if (timeframe === 'week') {
            return data.map(item => {
                const year = Math.floor(item.day / 100);
                const week = item.day % 100;
                const date = moment().year(year).isoWeek(week).startOf('isoWeek').format('YYYY-MM-DD');
                return { ...item, day: date };
            });
        } else if (timeframe === 'month') {
            return data.map(item => {
                if (typeof item.day !== 'string' || !moment(moment(new Date(item.day)).format('YYYY-MM-DD'), 'YYYY-MM-DD', true).isValid()) {
                    const year = Math.floor(item.day / 100);
                    const month = item.day % 100;
                    const date = moment().year(year).month(month).toDate();

                    return { ...item, day: date };
                }
                return item;
            });
        }

        return data;
    }, [timeframe]);

    useEffect(() => {
        if (!rows) return;
        
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
            chartInstanceRef.current = null;
        }

        const ctx = canvas.current;
        const convertedRows = convertToDate(rows);
        const convertedPrevRows = prevRows ? convertToDate(prevRows) : [];

        let config = {
            type: 'line',
            data: {
                labels: convertedRows.map(item => item.day),
                datasets: [
                    {
                        data: convertedRows.map(row => row.value),
                        fill: true,
                        backgroundColor: 'transparent',
                        borderColor: '#3CBBCE',
                        borderWidth: 2,
                        tension: 0,
                        pointRadius: 0,
                        pointHoverRadius: 3,
                        pointBackgroundColor: '#3CBBCE',
                        xAxisID: "x",
                        label: 'Period 1',
                    }, {
                        data: convertedPrevRows.map(row => row.value),
                        backgroundColor: 'transparent',
                        borderColor: '#9D8DD0',
                        borderWidth: 2,
                        tension: 0,
                        pointRadius: 0,
                        pointHoverRadius: 3,
                        pointBackgroundColor: '#9D8DD0',
                        xAxisID: "xPrev",
                        label: 'Period 2',
                    },
                ],
            },
            options: {
                animations: {
                    y: {
                        duration: 300,
                        easing: 'ease-out',
                    },
                },
                layout: {
                    padding: {
                        top: 0,
                        right: 0,
                        bottom: 5,
                        left: 0
                    },
                },
                scales: {
                    y: {
                        display: true,
                        position: 'left',
                        border: {
                            display: false,
                            dash: [0, 0]
                        },
                        grid: {
                            color: '#F2F2F2',
                            drawBorder: false,
                            borderColor: 'transparent',
                            borderWidth: 0
                        },
                        scaleLabel: {
                            display: true,
                            labelString: 'Total'
                        },
                        beginAtZero: true,
                        ticks: {
                            count: 4,
                            stepSize: null,
                            maxTicksLimit: 4,
                            callback: function (value, index, values) {
                                return formatNumber(value) + '    ';
                            },
                            font: {
                                size: 13,
                                family: 'Inter, sans-serif',
                                weight: '500',
                            },
                            color: '#A5A5A5',
                            crossAlign: 'far'
                        }
                    },
                    x: {
                        type: 'time',
                        border: {
                            display: false,
                            width: 0,
                            color: 'transparent'
                        },
                        grid: {
                            color: '#F2F2F2',
                            drawBorder: false,
                            borderColor: 'transparent',
                            borderWidth: 0,
                            drawOnChartArea: false
                        },
                        time: {
                            parser: 'YYYY-MM-DD',
                            unit: 'day',
                        },
                        scaleLabel: {
                            display: true,
                            labelString: 'Period 1'
                        },
                        ticks: {
                            maxTicksLimit: 0,
                            display: false,
                            callback: function (value, index, ticks) {
                                let label = this.getLabelForValue(value);
                                label = label.split("#")[0];
                                return label;
                            }
                        }
                    },
                    xPrev: {
                        type: 'time',
                        border: {
                            display: false,
                            width: 0,
                            color: 'transparent'
                        },
                        grid: {
                            color: '#F2F2F2',
                            drawBorder: false,
                            borderColor: 'transparent',
                            borderWidth: 0,
                            drawOnChartArea: false
                        },
                        time: {
                            parser: 'YYYY-MM-DD',
                            unit: 'day',
                        },
                        ticks: {
                            maxTicksLimit: 0,
                            display: false,
                            callback: function (value, index, ticks) {
                                let label = this.getLabelForValue(value);
                                label = label.split("#")[1];
                                return label;
                            }
                        }
                    }
                },
                interaction: {
                    intersect: false,
                    mode: 'index',
                },
                plugins: {
                    tooltip: {
                        backgroundColor: '#1A1C1D',
                        bodyFont: {
                            size: 13,
                            leading: 1.4
                        },
                        padding: {
                            top: 6,
                            right: 8,
                            bottom: 6,
                            left: 8
                        },
                        cornerRadius: 8,
                        displayColors: true,
                        usePointStyle: true,
                        boxWidth: 10,
                        boxPadding: 4,
                        bodySpacing: 2,
                        callbacks: {
                            title: (context) => { return null; },
                            label: function(context) {
                                let day = (context.dataset.xAxisID === "x" ? rows : prevRows)[context.dataIndex].day;
                                let value = formatNumber(context.parsed.y);

                                let year = Math.floor(day / 100) || moment(day).year();
                                let weekMonth = day % 100;
                                let start = null;
                                let end = null;
                                let yearFormat = year !== moment().year() ? year : '';

                                if (timeframe === 'week') {
                                    start = moment().year(year).isoWeek(weekMonth).startOf('isoWeek').format('MMM D');
                                    end = moment().year(year).isoWeek(weekMonth).endOf('isoWeek').format('MMM D');

                                    return `${value}   ${start} - ${end} ${yearFormat}`;
                                } else if (timeframe === 'month') {
                                    start = moment().year(year).month(weekMonth - 1).format('MMM');
                                    yearFormat = year;

                                    return `${value}   ${start} ${yearFormat}`;
                                }

                                let showYear = periodSelected ? periodSelected.title === 'All time' : false;
                                return `${value}   ${moment(day).format(`MMM D ${showYear || yearFormat ? 'YYYY' : ''}`)}`;
                            }

                        }
                    }

                },
                responsive: true,
                maintainAspectRatio: false,
            },
        }
        chartInstanceRef.current = new Chart(ctx, config);
        
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
                chartInstanceRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, prevRows, convertToDate, formatNumber, timeframe, periodSelected]);

    return (
        <div className="flex flex-col col-span-full sm:col-span-6 bg-white p-4 rounded-[12px]">
            <div className="text-[13px] font-semibold leading-[1.1] mb-3">{title}</div>
            {/* Chart built with Chart.js 3 */}
            <ChartLoadingPlaceholder chart={<canvas ref={canvas}></canvas>} isLoading={isLoading} />
            <div className={`flex flex-row text-[13px] font-medium leading-[1.2] ${isLoading ? 'text-[#E8E8E8]' : 'text-brand-green'} transition ease-in`}>
                <div>{moment(periodStart).format("MMM D")}</div>
                <div className={"flex-1"}></div>
                <div>{moment(periodEnd).subtract(1, 'day').format("MMM D")}</div>
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    if (prevProps.isLoading !== nextProps.isLoading) {
        return false;
    }
    
    if (prevProps.title !== nextProps.title || 
        prevProps.timeframe !== nextProps.timeframe || 
        prevProps.currency !== nextProps.currency) {
        return false;
    }
    
    if (prevProps.periodStart !== nextProps.periodStart || 
        prevProps.periodEnd !== nextProps.periodEnd) {
        return false;
    }
    
    if (prevProps.data !== nextProps.data) {
        return false;
    }
    
    return true;
});

export default BaseTimeseriesAPIChart;
